import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, ClipboardList, Users, Building2, HeadphonesIcon } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import CtaBand from "@/components/CtaBand";
import Seo from "@/components/Seo";
import { HOW_IT_WORKS, MANAGED_JOURNEY } from "@/lib/content";

const stepIcons = [ClipboardList, Users, Building2, HeadphonesIcon];

const HowItWorks = () => {
  return (
    <>
      <Seo
        title="How It Works — Hire or Build a Team in India | Bright EOR"
        description="From a single dedicated hire to a complete offshore function. See how Bright EOR recruits, employs and supports India-based teams for UK businesses."
        path="/how-it-works"
      />
      <PageHeader
        eyebrow="How it works"
        title="A simple way to build your India team."
        subtitle="From role brief to onboarding — Bright EOR runs the structure while your team member reports into you day to day."
      />
      <section className="py-20 lg:py-28" data-testid="how-detail">
        <div className="mx-auto max-w-5xl px-6 md:px-10 lg:px-12 space-y-24">
          {HOW_IT_WORKS.map((step, i) => {
            const Icon = stepIcons[i];
            return (
              <div
                key={step.step}
                className={`grid gap-10 lg:gap-16 items-center lg:grid-cols-2 ${
                  i % 2 === 1 ? "lg:[&>div:first-child]:order-2" : ""
                }`}
                data-testid={`step-detail-${step.step}`}
              >
                <div>
                  <p className="text-xs uppercase tracking-[0.22em] text-aqua font-semibold">Step {step.step}</p>
                  <h2 className="font-display mt-4 text-3xl sm:text-4xl font-semibold text-navy leading-[1.08] tracking-tight">
                    {step.title}
                  </h2>
                  <p className="mt-5 text-lg text-slateink leading-relaxed">{step.body}</p>
                </div>
                <div className="relative rounded-3xl bg-navy p-10 aspect-square lg:aspect-auto lg:min-h-[320px] overflow-hidden flex items-center justify-center">
                  <div className="absolute inset-0 grid-bg opacity-40" aria-hidden />
                  <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-aqua/20 blur-3xl" aria-hidden />
                  <div className="relative flex flex-col items-center text-white">
                    <div className="w-20 h-20 rounded-2xl bg-aqua text-navy flex items-center justify-center shadow-aqua-glow">
                      <Icon className="h-9 w-9" />
                    </div>
                    <p className="font-display mt-6 text-6xl font-semibold text-aqua/80">{step.step}</p>
                    <p className="mt-2 text-sm tracking-tight text-white/70">of four</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="mx-auto max-w-4xl px-6 mt-20">
          <div className="rounded-3xl bg-aqua/5 border border-aqua/30 p-8 lg:p-10 text-center">
            <h3 className="font-display text-2xl font-semibold text-navy">Your team member works for you. We support the structure.</h3>
            <p className="mt-3 text-slateink max-w-2xl mx-auto">
              You manage day-to-day tasks and performance. Bright EOR handles employment setup, payroll coordination, onboarding and the offshore working structure — so remote hiring actually works.
            </p>
            <Link
              to="/contact"
              data-testid="how-cta"
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-navy px-6 py-3 text-[15px] font-medium text-white hover:bg-aqua hover:text-navy transition-all"
            >
              Book a Consultation
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Managed-team journey */}
      <section className="bg-navy text-white py-20 lg:py-28 relative overflow-hidden" data-testid="how-managed-journey">
        <div className="absolute inset-0 grid-bg opacity-40" aria-hidden />
        <div className="absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full bg-aqua/10 blur-3xl" aria-hidden />
        <div className="relative mx-auto max-w-7xl px-6 md:px-10 lg:px-12">
          <div className="max-w-3xl">
            <p className="text-xs uppercase tracking-[0.22em] text-aqua font-semibold">Building a team or function?</p>
            <h2 className="font-display mt-4 text-4xl sm:text-5xl font-semibold tracking-tight leading-[1.05]">
              The managed-team journey.
            </h2>
            <p className="mt-6 text-lg text-white/75 leading-relaxed">
              For pods and full offshore functions we follow a structured build. You can still begin with one employee and add people as the model proves itself.
            </p>
          </div>
          <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {MANAGED_JOURNEY.map((s) => (
              <div key={s.step} className="rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-xl p-6" data-testid={`how-managed-step-${s.step}`}>
                <span className="font-display text-2xl font-semibold text-aqua">{s.step}</span>
                <h3 className="font-display mt-3 text-[16px] font-semibold tracking-tight">{s.title}</h3>
                <p className="mt-2 text-[13.5px] text-white/70 leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>
          <Link to="/managed-teams" className="mt-10 inline-flex items-center gap-2 rounded-full bg-aqua px-6 py-3 text-[15px] font-medium text-navy hover:shadow-aqua-glow transition-all" data-testid="how-managed-cta">
            Explore Managed Teams <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      <CtaBand />
    </>
  );
};

export default HowItWorks;
